/**
 * B4 — map Sellix enrollment journeys onto Signals RNC card/detail fields.
 * Keeps existing UI shapes; fills campaign/status/step from enrollments.
 */
import { formatDateTime } from "@/lib/format";
import {
  CONDITION_BRANCH_ELSE,
  CONDITION_BRANCH_IF,
} from "@/lib/campaignWorkflow";
import { flattenWorkflow } from "@/pages/signals/rnc/rncData";

const BADGE = {
  start: "START",
  email: "EMAIL",
  whatsapp: "WHATSAPP",
  sms: "SMS",
  telegram: "TELEGRAM",
  linkedin: "LINKEDIN",
  voice: "VOICE",
  wait: "WAIT",
  condition: "IF/ELSE",
  goal: "GOAL",
};

/**
 * Convert React Flow campaign snapshot → Signals FlowCanvas `{ steps }` shape.
 */
export function snapshotToSignalWorkflow(snapshot, { name, version } = {}) {
  if (!snapshot || typeof snapshot !== "object") {
    return { id: null, name: name || "Workflow", version: version || null, steps: [] };
  }
  const nodes = Array.isArray(snapshot.nodes) ? snapshot.nodes : [];
  const edges = Array.isArray(snapshot.edges) ? snapshot.edges : [];

  const steps = _linearizeSnapshotSteps(nodes, edges);

  return {
    id: version || name || "enrollment-snapshot",
    name: name || "Campaign workflow",
    version: version || null,
    steps,
  };
}

const BRANCH_LABEL_IF = "(If)";
const BRANCH_LABEL_ELSE = "(Else)";

function _buildOutgoingMap(nodes, edges) {
  const byId = Object.fromEntries(
    nodes.filter((n) => n?.id).map((n) => [String(n.id), n]),
  );
  const outgoing = Object.fromEntries(Object.keys(byId).map((id) => [id, []]));
  for (const edge of edges) {
    const source = String(edge?.source || "");
    const target = String(edge?.target || "");
    if (!source || !target || !byId[source] || !byId[target]) continue;
    outgoing[source].push({
      target,
      handle: String(edge.sourceHandle || edge.data?.branch || "").toLowerCase(),
    });
  }
  return { byId, outgoing };
}

function _branchHead(outgoing, conditionId, handle) {
  return (outgoing[conditionId] || []).find((edge) => edge.handle === handle)?.target || null;
}

function _singleSuccessor(nodeId, outgoing) {
  const outs = outgoing[nodeId] || [];
  if (outs.length !== 1) return null;
  return outs[0].target;
}

function _distanceMap(startId, outgoing) {
  const dist = new Map();
  if (!startId) return dist;
  const queue = [startId];
  dist.set(startId, 0);
  while (queue.length) {
    const id = queue.shift();
    for (const edge of outgoing[id] || []) {
      if (!dist.has(edge.target)) {
        dist.set(edge.target, dist.get(id) + 1);
        queue.push(edge.target);
      }
    }
  }
  return dist;
}

/** First node reachable from both If and Else branch heads (merge / join point). */
function _findMergeNode(conditionId, outgoing) {
  const ifHead = _branchHead(outgoing, conditionId, CONDITION_BRANCH_IF)
    || _branchHead(outgoing, conditionId, "yes");
  const elseHead = _branchHead(outgoing, conditionId, CONDITION_BRANCH_ELSE)
    || _branchHead(outgoing, conditionId, "no");
  if (!ifHead || !elseHead) return null;

  const ifDist = _distanceMap(ifHead, outgoing);
  const elseDist = _distanceMap(elseHead, outgoing);
  const common = [...ifDist.keys()].filter((id) => elseDist.has(id));
  if (!common.length) return null;

  common.sort((a, b) => {
    const scoreA = Math.max(ifDist.get(a), elseDist.get(a));
    const scoreB = Math.max(ifDist.get(b), elseDist.get(b));
    return scoreA - scoreB;
  });
  return common[0];
}

function _collectBranchSteps(headId, stopAt, outgoing, byId) {
  const branchSteps = [];
  _walkLinearSteps(headId, stopAt, outgoing, byId, branchSteps);
  return branchSteps;
}

function _handleConditionNode(current, outgoing, byId, stepsOut) {
  const merge = _findMergeNode(current, outgoing);
  const ifHead = _branchHead(outgoing, current, CONDITION_BRANCH_IF)
    || _branchHead(outgoing, current, "yes");
  const elseHead = _branchHead(outgoing, current, CONDITION_BRANCH_ELSE)
    || _branchHead(outgoing, current, "no");

  for (const step of _collectBranchSteps(ifHead, merge, outgoing, byId)) {
    stepsOut.push({ ...step, branchLabel: BRANCH_LABEL_IF });
  }
  for (const step of _collectBranchSteps(elseHead, merge, outgoing, byId)) {
    stepsOut.push({ ...step, branchLabel: BRANCH_LABEL_ELSE });
  }
  return merge;
}

function _walkLinearSteps(startId, stopAt, outgoing, byId, stepsOut) {
  let current = startId;
  while (current && current !== stopAt) {
    const node = byId[current];
    if (!node) break;
    const type = String(node.type || "").toLowerCase();

    if (type === "condition" || type === "ab") {
      stepsOut.push(_buildStepFromNode(node));
      current = _handleConditionNode(current, outgoing, byId, stepsOut);
      continue;
    }

    if (type === "goal") {
      stepsOut.push(_buildStepFromNode(node));
      break;
    }

    stepsOut.push(_buildStepFromNode(node));
    current = _singleSuccessor(current, outgoing);
  }
}

function _linearizeSnapshotSteps(nodes, edges) {
  const { byId, outgoing } = _buildOutgoingMap(nodes, edges);
  if (!Object.keys(byId).length) return [];

  const targets = new Set();
  for (const list of Object.values(outgoing)) {
    for (const edge of list) targets.add(edge.target);
  }

  const startId = Object.keys(byId).find((id) => (byId[id].type || "").toLowerCase() === "start")
    || Object.keys(byId).find((id) => !targets.has(id))
    || Object.keys(byId)[0];

  const steps = [];
  _walkLinearSteps(startId, null, outgoing, byId, steps);
  return steps;
}

function _getSpecificStepData(type, id, title, detail, data) {
  if (type === "start") {
    return { kind: "action", badge: BADGE.start, title: data.label || "Enrollment", detail: data.trigger || "" };
  }
  if (type === "condition" || type === "ab") {
    return { kind: "condition", badge: BADGE.condition || "IF/ELSE" };
  }
  if (type === "goal") {
    return { kind: "goal", badge: BADGE.goal, detail: "" };
  }
  if (type === "wait") {
    return { kind: "action", badge: BADGE.wait, detail: detail || "Wait" };
  }
  return {
    kind: "action",
    badge: BADGE[type] || String(type).toUpperCase(),
    detail: typeof detail === "string" ? detail : "",
  };
}

function _buildStepFromNode(node) {
  const type = String(node.type || "action");
  const data = node.data && typeof node.data === "object" ? node.data : {};
  const title = data.label || type;
  const detail = data.subject || data.message || data.condition || "";
  const id = node.id != null ? String(node.id) : undefined;
  
  return { id, title, ..._getSpecificStepData(type, id, title, detail, data) };
}

function _getNextStep(uiStatus, rnc) {
  if (uiStatus.startsWith("Qualified") || uiStatus.startsWith("Unqualified")) {
    return "—";
  }
  return rnc.nextStep || "—";
}

function _getRemainingTime(enrollment, rnc) {
  if (enrollment.next_run_at) {
    return `Next: ${formatDateTime(enrollment.next_run_at)}`;
  }
  return rnc.remainingTime || "—";
}

function getEmptyJourneyProps(rnc) {
  return {
    campaignId: null,
    campaignName: null,
    workflowId: null,
    workflowAssigned: false,
    signalWorkflow: null,
    status: null,
    progress: null,
    currentStep: rnc.currentStep || null,
    stepIndex: 0,
    enrollmentId: null,
    leadStatus: null,
    exited: false,
    exitReason: null,
    stepFailures: {},
    stepSkips: {},
    lastError: null,
    channel: rnc.channel || "rnc",
    nextStep: "None",
  };
}

// ─── timelineStepState helpers ────────────────────────────────────────────────

function _isTerminalStatus(status) {
  const label = String(status || "");
  return label.startsWith("Qualified") || label.startsWith("Unqualified");
}

function _resolveFailureOrSkip(index, stepIndex, stepId, failures, skips, status) {
  const failure = stepId && failures[stepId] ? failures[stepId] : null;
  const skip = !failure && stepId && skips[stepId] ? skips[stepId] : null;
  const pastOrTerminal = index < stepIndex || _isTerminalStatus(status);
  const atOrPast = pastOrTerminal || index === stepIndex;
  if (failure && atOrPast) {
    return { cls: "failed", when: "Failed", error: String(failure.error || failure.reason || "Send failed") };
  }
  if (skip && atOrPast) {
    return { cls: "skipped", when: "Skipped", error: String(skip.message || skip.reason || "Step skipped") };
  }
  return null;
}

function _resolveExitedState(index, stepIndex) {
  if (index < stepIndex) return { cls: "done", when: "Completed" };
  if (index === stepIndex) return { cls: "pending", when: "Exited", stopped: true };
  return { cls: "pending", when: "Cancelled" };
}

/**
 * Outreach timeline state for one workflow step.
 * An exited lead never has a live step: steps before the exit point stay done,
 * the exit point is marked, and everything after it is cancelled.
 * Failed sends stay red; address/channel skips stay yellow.
 */
export function timelineStepState({
  index,
  stepIndex = 0,
  status,
  exited = false,
  stepId,
  stepFailures = null,
  stepSkips = null,
  stepBadge = null,
  waiting = false,
}) {
  const failures = stepFailures && typeof stepFailures === "object" ? stepFailures : {};
  const skips = stepSkips && typeof stepSkips === "object" ? stepSkips : {};

  const failureOrSkip = _resolveFailureOrSkip(index, stepIndex, stepId, failures, skips, status);
  if (failureOrSkip) return failureOrSkip;

  if (_isTerminalStatus(status)) return { cls: "done", when: "Completed" };

  if (exited) return _resolveExitedState(index, stepIndex);

  if (index < stepIndex) return { cls: "done", when: "" };
  if (index === stepIndex) {
    const isWaitStep = String(stepBadge || "").toUpperCase() === "WAIT";
    if (isWaitStep && waiting) {
      return { cls: "active", when: "Waiting" };
    }
    return { cls: "active", when: "In progress" };
  }
  return { cls: "pending", when: "Pending" };
}

function resolveRemainingTime(rnc, enrollment, stopped) {
  if (stopped) return "—";
  if (enrollment.next_run_at) return `Next: ${formatDateTime(enrollment.next_run_at)}`;
  return rnc.remainingTime || "—";
}

function normalizeStepLabel(value) {
  return String(value || "").trim().toLowerCase();
}

function stepLabelsMatch(stepTitle, currentLabel) {
  const title = normalizeStepLabel(stepTitle);
  const label = normalizeStepLabel(currentLabel);
  if (!title || !label) return false;
  return title === label || title.includes(label) || label.includes(title);
}

/**
 * Resolve the active outreach timeline index from enrollment state.
 * Prefer current_step_label (same source as Assigned Workflow) so wait steps
 * stay current while their timer runs, even if step_index advanced early.
 */
export function resolveTimelineStepIndex(flatSteps, enrollment) {
  if (!Array.isArray(flatSteps) || !flatSteps.length) {
    return enrollment?.step_index ?? 0;
  }

  const terminalLabel = normalizeStepLabel(enrollment?.current_step_label);
  if (terminalLabel === "closed" || terminalLabel === "exited") {
    return enrollment?.step_index ?? flatSteps.length;
  }

  const currentLabel = enrollment?.current_step_label;
  if (currentLabel) {
    const byLabel = flatSteps.findIndex((step) => stepLabelsMatch(step.title, currentLabel));
    if (byLabel >= 0) return byLabel;
  }

  const nodeId = enrollment?.current_node_id;
  if (nodeId) {
    const byId = flatSteps.findIndex((step) => step.id === nodeId);
    if (byId >= 0) return byId;
  }

  const fallback = enrollment?.step_index ?? 0;
  return Math.min(Math.max(fallback, 0), flatSteps.length - 1);
}

function isEnrollmentWaiting(enrollment) {
  if (!enrollment?.next_run_at) return false;
  const due = new Date(enrollment.next_run_at);
  return !Number.isNaN(due.getTime()) && due.getTime() > Date.now();
}

function isEnrollmentStopped(uiStatus, exited) {
  return exited
    || uiStatus.startsWith("Qualified")
    || uiStatus.startsWith("Unqualified");
}

function computeNextStep(stopped, timelineStepIndex, flatSteps) {
  if (stopped) return "—";
  const nextIdx = timelineStepIndex + 1;
  if (flatSteps && nextIdx < flatSteps.length) {
    return flatSteps[nextIdx]?.title || "Qualification Decision";
  }
  return "Qualification Decision";
}

function buildEnrollmentFields(enrollment, rnc) {
  return {
    currentNodeId: enrollment.current_node_id || null,
    nextRunAt: enrollment.next_run_at || null,
    channel: enrollment.channel || rnc.channel || "rnc",
    assignedDate: enrollment.enrolled_at || rnc.assignedDate,
    lastActivity: enrollment.updated_at || rnc.lastActivity,
    exitReason: enrollment.exit_reason || null,
    statusComment: enrollment.status_comment || null,
    stepFailures: enrollment.step_failures || {},
    stepSkips: enrollment.step_skips || {},
    lastError: enrollment.last_error || null,
  };
}

export function applyJourneyToRnc(rnc, journey) {
  if (!rnc) return rnc;
  const enrollment = journey?.enrollment;
  if (!enrollment || journey?.status !== "enrolled") {
    return { ...rnc, ...getEmptyJourneyProps(rnc) };
  }

  const signalWorkflow = snapshotToSignalWorkflow(enrollment.workflow_snapshot, {
    name: enrollment.campaign_name,
  });

  const flatSteps = flattenWorkflow(signalWorkflow);
  const timelineStepIndex = resolveTimelineStepIndex(flatSteps, enrollment);

  const uiStatus = enrollment.ui_status || "";
  const exited = Boolean(enrollment.exited) || uiStatus === "Exited";
  const stopped = isEnrollmentStopped(uiStatus, exited);
  const waiting = isEnrollmentWaiting(enrollment);
  const nextStep = computeNextStep(stopped, timelineStepIndex, flatSteps);

  return {
    ...rnc,
    campaignId: enrollment.campaign_id,
    campaignName: enrollment.campaign_name,
    workflowId: enrollment.campaign_id,
    workflowAssigned: true,
    signalWorkflow,
    status: uiStatus || null,
    progress: enrollment.progress ?? null,
    currentStep: enrollment.current_step_label || rnc.currentStep,
    stepIndex: timelineStepIndex,
    workflowWaiting: waiting,
    enrollmentId: enrollment.id,
    leadStatus: enrollment.lead_status,
    nextStep,
    exited,
    remainingTime: resolveRemainingTime(rnc, enrollment, stopped),
    ...buildEnrollmentFields(enrollment, rnc),
  };
}
